import React, { Component } from 'react'
import '../../styles/GlobalStyle.css'
import { db, firebase } from '../../firebase/Firebase'
import ColType from '../.././Types'
import { Layout, Select, Icon, Tabs, Button, notification, Collapse, Popconfirm, Checkbox } from 'antd'
import { getQueryStringParam, flattenDoc, cleverStartQuiz, getQuarterAndWeekFromNum, shuffleArray, manualCheckAssessment, convertUndefinedToNull } from '../../Util'
import { Link } from 'react-router-dom'
import DocumentDelegator from '../../edviz/EdvizDelegator'
import PacingGuideUploads from './PacingGuideUploads'

class StudentResource extends Component {
    state = {
        quizReady: false,
        keyIdx: 0,
    }

    componentDidMount() {
        // Listen for resize messages from iframe
        window.addEventListener('message', this.handleIframeMessage);

        // Read-aloud must not outlive the page it was started on. Unmount covers
        // route changes that leave this component; the href check covers URL
        // changes (tab/query switches) where it stays mounted, since React Router
        // pushState navigation fires no browser event.
        this.startHref = window.location.href;
        this.hrefWatch = setInterval(() => {
            if (window.location.href !== this.startHref) {
                this.stopSpeech();
                this.startHref = window.location.href;
            }
        }, 300);
    }

    stopSpeech = () => {
        try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) { /* ignore */ }
        try {
            this.iframeRef && this.iframeRef.contentWindow &&
                this.iframeRef.contentWindow.postMessage({ type: 'dotit.tts.stop' }, '*');
        } catch (e) { /* ignore */ }
    }

    componentWillUnmount() {
        window.removeEventListener('message', this.handleIframeMessage);
        clearInterval(this.hrefWatch);
        this.stopSpeech();

        // Clean up iframe observer if it exists
        if (this.iframeObserver) {
            this.iframeObserver.disconnect();
        }
    }

    componentWillReceiveProps(nextProps) {
        // If the htmlSource changes, reset the quizReady state
        if (nextProps.htmlSource !== this.props.htmlSource) {
            this.pendingState = null; // Reset pending state
            this.setState({ quizReady: false, keyIdx: this.state.keyIdx + 1 });
        }
    }

    handleIframeMessage = (event) => {
        if (!event.data || !event.data.type) {
            return;
        }

        // firestore doesn't accept undefined values, so first
        // convert undefined values to null
        if (event.data.type === 'dotit.resource.ready') {
            this.setState({ quizReady: true });

            // Load saved state if provided
            if (this.props.savedState) {
                // Use setTimeout to ensure the quiz is fully initialized
                setTimeout(() => {
                    this.loadQuizState(this.props.savedState);
                }, 100);
            }

            if (this.props.onReady) {
                this.props.onReady(convertUndefinedToNull(event.data));
            }
        } else if (event.data.type === 'dotit.resource.updateState') {
            if (this.props.onUpdate) {
                this.props.onUpdate(convertUndefinedToNull(event.data));
            }
        } else if (event.data.type === 'dotit.resource.submit') {
            if (this.props.onSubmit) {
                this.props.onSubmit(convertUndefinedToNull(event.data));
            }
        } else if (event.data && event.data.type === 'iframe-resize') {
            const iframe = this.iframeRef;
            if (iframe) {
                iframe.style.setProperty('--js-height', event.data.height + 'px');
                iframe.style.height = 'var(--js-height)';
            }
        } else {
            console.log("unhandled iframe message", event.data);
        }
    }

    // Method to load quiz state
    loadQuizState = (savedState) => {
        if (!this.state.quizReady || !this.iframeRef) {
            console.warn('Quiz not ready or iframe not available');
            return;
        }

        // Send the LOAD_STATE message to the quiz iframe
        this.iframeRef.contentWindow.postMessage({
            type: 'dotit.resource.loadState', // This matches MESSAGE_TYPES.LOAD_STATE in your quiz
            data: savedState
        }, '*');
    }

    // Public method to load state externally
    loadState = (savedState) => {
        if (this.state.quizReady) {
            this.loadQuizState(savedState);
        } else {
            // If quiz isn't ready yet, wait for it
            this.pendingState = savedState;
        }
    }

    // Public method to scroll to a specific question by data-label
    scrollToQuestion = (dataLabel) => {
        if (!this.iframeRef) {
            console.warn('Quiz not ready or iframe not available');
            return;
        }

        // Send scroll message to the iframe
        this.iframeRef.contentWindow.postMessage({
            type: 'dotit.resource.scrollTo',
            dataLabel: dataLabel
        }, '*');
    }

    // Also check for pending state when quiz becomes ready
    componentDidUpdate(prevProps, prevState) {
        if (!prevState.quizReady && this.state.quizReady && this.pendingState) {
            setTimeout(() => {
                this.loadQuizState(this.pendingState);
                this.pendingState = null;
            }, 100);
        }

        // Reset iframe height when htmlSource changes
        if (prevProps.htmlSource !== this.props.htmlSource && this.iframeRef) {
            this.iframeRef.style.setProperty('--js-height', '0px');
            this.iframeRef.style.height = 'var(--js-height)';
        }
    }

    resizeIframe = () => {
        const iframe = this.iframeRef;
        if (iframe && iframe.contentDocument) {
            try {
                const height = iframe.contentDocument.documentElement.scrollHeight;
                iframe.style.setProperty('--js-height', height + 'px');
                iframe.style.height = 'var(--js-height)';
            } catch (error) {
                console.warn('Could not resize iframe:', error);
            }
        }
    }

    handleIframeLoad = () => {
        /* ---------- 1. Clean‑up anything from the *previous* iframe ---------- */
        if (this.iframeObserver) {
            this.iframeObserver.disconnect();
            this.iframeObserver = null;
        }

        if (this.prevIframeContentWindow) {
            // We stored this pointer the last time we attached the listener
            try {
                this.prevIframeContentWindow.removeEventListener('resize', this.resizeIframe);
            } catch (err) {
                console.warn('Could not remove previous iframe resize listener:', err);
            }
            this.prevIframeContentWindow = null;
        }

        /* ---------- 2. Set up the *current* iframe ---------- */
        const iframe = this.iframeRef;
        if (!iframe || !iframe.contentDocument) {
            return; // Nothing to do
        }

        // Keep a reference so we can detach the listener next time
        this.prevIframeContentWindow = iframe.contentWindow;

        try {
            if (this.props.removeSections) {
                this.props.removeSections.forEach((selector) => {
                    const element = iframe.contentDocument.querySelector(selector);
                    if (element) {
                        element.remove();
                    }
                });
            }
            if (this.props.addClassNameToLastQuestion) {
                const questions = iframe.contentDocument.querySelectorAll('.quiz-question');
                if (questions.length > 0) {
                    const lastQuestion = questions[questions.length - 1];
                    this.props.addClassNameToLastQuestion.split(' ').forEach((className) => {
                        lastQuestion.classList.add(className);
                    });
                }
            }
            /* Initial resize */
            this.resizeIframe();

            /* MutationObserver to watch for DOM changes inside the iframe */
            const observer = new MutationObserver(() => this.resizeIframe());
            observer.observe(iframe.contentDocument.body, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeOldValue: true
            });
            this.iframeObserver = observer;

            /* Listen for window‑resize events inside the iframe */
            iframe.contentWindow.addEventListener('resize', this.resizeIframe);
        } catch (error) {
            console.warn('Could not set up iframe resize observer:', error);
        }
    };

    render() {
        // Conditionally add copy-paste prevention styles
        const copyPasteStyles = this.props.noCopyPaste ? `
                    /* Disable text selection */
                    user-select: none;
                    -webkit-user-select: none;
                    -moz-user-select: none;
                    -ms-user-select: none;` : '';

        // Conditionally add copy-paste prevention scripts
        const copyPasteScript = this.props.noCopyPaste ? `
            // ============================================
            // Copy-Paste Prevention
            // ============================================
            function preventCopyPaste(e) {
                e.preventDefault();
                return false;
            }

            function preventCopyPasteKeyDown(e) {
                if (e.ctrlKey || e.metaKey) {
                    // Prevent Ctrl+A (Select All)
                    if (e.key === 'a' || e.keyCode === 65) {
                        e.preventDefault();
                        return false;
                    }
                    // Prevent Ctrl+C (Copy)
                    if (e.key === 'c' || e.keyCode === 67) {
                        e.preventDefault();
                        return false;
                    }
                    // Prevent Ctrl+X (Cut)
                    if (e.key === 'x' || e.keyCode === 88) {
                        e.preventDefault();
                        return false;
                    }
                }
            }

            // Add event listeners for copy-paste prevention
            document.addEventListener('copy', preventCopyPaste);
            document.addEventListener('cut', preventCopyPaste);
            document.addEventListener('paste', preventCopyPaste);
            document.addEventListener('contextmenu', preventCopyPaste);
            document.addEventListener('keydown', preventCopyPasteKeyDown);
` : '';

        // Conditionally turn on Edviz's built-in read-aloud (text-to-speech)
        // controls. Read-aloud defaults to OFF in every quiz; this is the
        // per-render opt-in (e.g. for a district that asks for it).
        const enableTTSScript = this.props.enableTTS ? `
            <script>
                window.EdvizConfig = window.EdvizConfig || {};
                window.EdvizConfig.enableTTS = true;
            </script>` : '';

        // Conditionally disable Edviz's built-in two-pane passage/questions
        // split view. The split view defaults to ON for any quiz with a
        // passage; this is the per-render escape hatch to fall back to the
        // original single-column layout.
        const disableSplitViewScript = this.props.disableSplitView ? `
            <script>
                window.EdvizConfig = window.EdvizConfig || {};
                window.EdvizConfig.disableSplitView = true;
            </script>` : '';

        // Inject resize script and conditionally add copy-paste prevention
        let htmlWithResizeScript = this.props.htmlSource.replace(
            '<head>',
            `<head>${enableTTSScript}${disableSplitViewScript}
            <style>
                html, body {
                    margin: 0;
                    padding: 0;
                    overflow: hidden;${copyPasteStyles}
                }

                .MathJax,
                .MathJax *,
                mjx-container,
                mjx-container * {
                    pointer-events: none !important;
                }

                .number-input {
                    width: 140px !important;
                }

                @media print {
                    html, body {
                        height: auto !important;
                        min-height: 0 !important;
                        overflow: visible !important;
                        background: #fff !important;
                        display: block !important;
                    }

                    * {
                        background: #fff !important;
                        color: #000 !important;
                        border-color: #000 !important;
                        border-size: 1px !important;
                        break-inside: avoid !important;
                        page-break-inside: avoid !important;
                        box-shadow: none !important;
                        overflow: visible !important;
                        height: auto !important;
                    }

                    .edviz-quiz-page {
                        display: block !important;
                    }

                    .quiz-question, .question-header-container, h1, 
                    h2, h3, h4, h5, h6, p, div, span, input, textarea, label,
                    .radio-option, .emoji, .quiz-survey-container,
                    .question-header, .question-standard, .MathJax,
                    .mcq-options, .question-body, .edviz-section {
                        break-inside: avoid !important;
                        page-break-inside: avoid !important;
                    }

                    p, div, label { orphans: 3 !important; widows: 3 !important; }

                    body > *:last-child,
                    .content > *:last-child {
                        margin-bottom: 0 !important;
                    }

                    .page:last-child {
                        break-after: auto !important;
                        page-break-after: auto !important;
                    }

                    .edviz-container {
                        border: none !important;
                        overflow: visible !important;
                    }

                    .emoji {
                        filter: grayscale(100%) !important;
                    }

                    input::placeholder {
                        color: transparent !important;
                    }

                    textarea::placeholder {
                        color: transparent !important;
                    }

                    .radio-option, input {
                        border: 1px solid #000 !important;
                    }

                    .radio-option {
                        display: block !important;
                    }

                    #submission-section, .edviz-button.check-button,
                    #progress-section, #final-submit-section {
                        display: none !important;
                    }

                    .question-header-container {
                        display: block !important;
                        width: 100% !important;
                    }

                    .question-standard {
                        background: #fff !important;
                        border: none !important;
                        padding: 0px !important;
                    }

                    .edviz-table {
                        border: 1px solid #000 !important;
                        border-collapse: initial !important;
                    }

                    .edviz-table th {
                        border-bottom: 1px solid #000 !important;
                    }

                    .interactive-part, .review-content {
                        border: none !important;
                        padding: 0px !important;
                    }

                    .review-content {
                        margin-bottom: 16px !important;
                    }
                }
            </style>`
        ).replace(
            '</body>',
            `
        <script>${copyPasteScript}

            // ============================================
            // Resize functionality
            // ============================================
            let resizeTimeout;
            let lastHeight = 0;

            function getTrueHeight() {
                const body = document.body;
                const html = document.documentElement;
                
                const heights = [
                    body.scrollHeight,
                    body.offsetHeight,
                    html.clientHeight,
                    html.scrollHeight,
                    html.offsetHeight
                ];
                
                return Math.max(...heights);
            }

            function notifyParentOfResize() {
                const height = getTrueHeight();
                lastHeight = height;
                window.parent.postMessage({
                    type: 'iframe-resize',
                    height: height
                }, '*');
            }

            function forceResize() {
                document.body.style.height = 'auto';
                notifyParentOfResize();
            }

            function debouncedResize() {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(forceResize, 50);
            }

            function scrollToElementByDataLabel(dataLabel) {
                try {
                    const element = document.querySelector('[data-label="' + dataLabel + '"]');
                    if (element) {
                        element.scrollIntoView({ 
                            behavior: 'instant', 
                            block: 'start',
                            inline: 'nearest' 
                        });
                        return true;
                    } else {
                        console.warn('Element with data-label "' + dataLabel + '" not found');
                        return false;
                    }
                } catch (error) {
                    console.error('Error scrolling to element:', error);
                    return false;
                }
            }

            window.addEventListener('message', function(event) {
                if (event.data && event.data.type === 'dotit.resource.scrollTo') {
                    scrollToElementByDataLabel(event.data.dataLabel);
                }
            });

            window.addEventListener('load', function() {
                setTimeout(forceResize, 100);
            });

            document.addEventListener('DOMContentLoaded', forceResize);

            if (window.MutationObserver) {
                const observer = new MutationObserver(debouncedResize);
                observer.observe(document.body, {
                    childList: true,
                    subtree: true,
                    attributes: true
                });
            } else {
                setInterval(forceResize, 1000);
            }

            window.addEventListener('resize', debouncedResize);
            window.addEventListener('content-changed', forceResize);
        </script>
        </body>`
        );

        if (this.props.additionalClassName) {
            htmlWithResizeScript = this.props.htmlSource.replace(
                '<body',
                `<body class="${this.props.additionalClassName || ''}"`
            )
        }

        return (
            <iframe
                key={'resource-' + this.state.keyIdx}
                ref={ref => this.iframeRef = ref}
                srcDoc={htmlWithResizeScript}
                title='Student Resource'
                width={'100%'}
                onLoad={this.handleIframeLoad}
                style={{
                    border: 'none',
                    overflow: 'hidden',
                    minHeight: '100px'
                }}
            >
            </iframe>
        )
    }
}

export default StudentResource